// rAF-driven tween + easing helpers. No GSAP — combat owns its own clock so
// it can pause / scale to speed multipliers and bail on AbortSignal.
//
// Each animation is a Promise<void> that resolves when complete, OR
// resolves early (without finishing the visual) if the AbortSignal fires.
// Callers can await several in sequence to script choreography.

export type EaseFn = (t: number) => number;

// Easing kit — minimal but expressive. Match Pixi/GSAP conventions.
export const Ease = {
  linear: (t: number) => t,
  outQuad: (t: number) => 1 - (1 - t) * (1 - t),
  inQuad: (t: number) => t * t,
  inOutQuad: (t: number) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2),
  outCubic: (t: number) => 1 - Math.pow(1 - t, 3),
  inOutCubic: (t: number) =>
    t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2,
  outBack: (t: number) => {
    const c1 = 1.70158;
    const c3 = c1 + 1;
    return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
  },
  outElastic: (t: number) => {
    const c4 = (2 * Math.PI) / 3;
    if (t === 0) return 0;
    if (t === 1) return 1;
    return Math.pow(2, -10 * t) * Math.sin((t * 10 - 0.75) * c4) + 1;
  },
  // Half-sine pulse: 0 → 1 → 0
  pulseSine: (t: number) => Math.sin(t * Math.PI),
} satisfies Record<string, EaseFn>;

// Minimum frame duration to keep things visible at 4× speed
const MIN_DURATION_MS = 10;

/**
 * rAF tween. Calls `onUpdate(value, t)` each frame with the eased value.
 * Honors AbortSignal — if aborted, resolves immediately without further
 * updates. The signal does NOT throw; combat playback should be skippable
 * gracefully, not error-ful.
 */
export function tween(opts: {
  from: number;
  to: number;
  durationMs: number;
  ease?: EaseFn;
  signal?: AbortSignal;
  onUpdate: (value: number, t: number) => void;
}): Promise<void> {
  const { from, to, ease = Ease.linear, signal, onUpdate } = opts;
  const durationMs = Math.max(MIN_DURATION_MS, opts.durationMs);

  return new Promise<void>((resolve) => {
    if (signal?.aborted) {
      onUpdate(to, 1);
      resolve();
      return;
    }

    const startTime = performance.now();
    let rafId = 0;

    function tick(now: number) {
      if (signal?.aborted) {
        onUpdate(to, 1);
        resolve();
        return;
      }
      const elapsed = now - startTime;
      const t = Math.min(1, elapsed / durationMs);
      const eased = ease(t);
      const value = from + (to - from) * eased;
      onUpdate(value, t);
      if (t >= 1) {
        resolve();
        return;
      }
      rafId = requestAnimationFrame(tick);
    }

    rafId = requestAnimationFrame(tick);

    if (signal) {
      const onAbort = () => {
        cancelAnimationFrame(rafId);
        onUpdate(to, 1);
        resolve();
      };
      signal.addEventListener('abort', onAbort, { once: true });
    }
  });
}

/**
 * Wait N ms, abort-aware. Returns early if the signal aborts.
 */
export function wait(ms: number, signal?: AbortSignal): Promise<void> {
  const durationMs = Math.max(MIN_DURATION_MS, ms);
  return new Promise<void>((resolve) => {
    if (signal?.aborted) return resolve();
    const t = setTimeout(resolve, durationMs);
    if (signal) {
      signal.addEventListener(
        'abort',
        () => {
          clearTimeout(t);
          resolve();
        },
        { once: true },
      );
    }
  });
}

/** Scale a duration by playback speed (1 = real-time, 2 = double, 4 = quad). */
export function scaledDuration(base: number, speed: number): number {
  return Math.max(MIN_DURATION_MS, base / Math.max(1, speed));
}
